// "All creators" tab on the admin Creators page: every approved creator
// on the platform, filterable client-side (the admin approved-creators
// endpoint returns the full set in one page — same source the composer's
// Step 2 picker uses). Per-row "View" opens the same quick-view dialog
// as the composer, with the preview share + full profile.

import { useMemo, useState } from "react";
import {
  Badge,
  Button,
  Card,
  CardContent,
  CardHeader,
  CardTitle,
} from "@greenhold/components";

import { EmptyState } from "../../components/EmptyState";
import {
  useAdminApprovedCreators,
  type ApprovedCreatorRow,
} from "./portfolios/useAdminApprovedCreators";
import { CreatorQuickViewDialog } from "./portfolios/CreatorQuickViewDialog";

function label(c: ApprovedCreatorRow): string {
  return c.display_name?.trim() || c.handle || c.user_id.slice(0, 8);
}

export function AllCreatorsPanel() {
  const query = useAdminApprovedCreators();
  const [search, setSearch] = useState("");
  const [region, setRegion] = useState("any");
  const [language, setLanguage] = useState("any");
  const [previewCreator, setPreviewCreator] =
    useState<ApprovedCreatorRow | null>(null);

  const all = useMemo(() => query.data?.data ?? [], [query.data]);

  // Filter options derived from the data itself — no hardcoded region/
  // language lists to drift out of sync with what creators actually have.
  const regions = useMemo(
    () =>
      Array.from(new Set(all.map((c) => c.region).filter(Boolean))).sort() as string[],
    [all],
  );
  const languages = useMemo(
    () =>
      Array.from(
        new Set(all.map((c) => c.language).filter(Boolean)),
      ).sort() as string[],
    [all],
  );

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    return all.filter((c) => {
      if (q) {
        const hay = `${c.display_name ?? ""} ${c.handle ?? ""}`.toLowerCase();
        if (!hay.includes(q)) return false;
      }
      if (region !== "any" && c.region !== region) return false;
      if (language !== "any" && c.language !== language) return false;
      return true;
    });
  }, [all, search, region, language]);

  return (
    <div className="space-y-[var(--gh-rhythm-admin,16px)]">
      {/* Filter bar */}
      <Card>
        <CardContent className="flex flex-wrap gap-3 items-end p-4">
          <label className="text-sm">
            <span className="block mb-1 opacity-70">Search</span>
            <input
              type="text"
              className="border rounded px-2 py-1 bg-white"
              placeholder="Name or handle"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </label>
          <label className="text-sm">
            <span className="block mb-1 opacity-70">Region</span>
            <select
              className="border rounded px-2 py-1 bg-white"
              value={region}
              onChange={(e) => setRegion(e.target.value)}
            >
              <option value="any">Any</option>
              {regions.map((r) => (
                <option key={r} value={r}>
                  {r}
                </option>
              ))}
            </select>
          </label>
          <label className="text-sm">
            <span className="block mb-1 opacity-70">Language</span>
            <select
              className="border rounded px-2 py-1 bg-white"
              value={language}
              onChange={(e) => setLanguage(e.target.value)}
            >
              <option value="any">Any</option>
              {languages.map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </select>
          </label>
        </CardContent>
      </Card>

      {query.isPending ? (
        <Card>
          <CardContent className="p-8 text-center opacity-70">
            Loading…
          </CardContent>
        </Card>
      ) : query.isError ? (
        <Card>
          <CardContent className="p-6 text-center space-y-3">
            <p className="text-destructive">{query.error.message}</p>
            <Button onClick={() => query.refetch()} variant="secondary">
              Retry
            </Button>
          </CardContent>
        </Card>
      ) : filtered.length === 0 ? (
        <EmptyState
          aria-label="No creators"
          description={
            all.length === 0
              ? "No approved creators yet."
              : "No creators match these filters."
          }
        />
      ) : (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">
              {filtered.length} of {all.length} creator
              {all.length === 1 ? "" : "s"}
            </CardTitle>
          </CardHeader>
          <CardContent className="p-0">
            <table className="w-full text-sm">
              <thead className="bg-brand-paper-100 text-left">
                <tr>
                  <th className="px-4 py-2">Display name</th>
                  <th className="px-4 py-2">Handle</th>
                  <th className="px-4 py-2">Region</th>
                  <th className="px-4 py-2">Approved</th>
                  <th className="px-4 py-2"></th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((c) => (
                  <tr
                    key={c.user_id}
                    className="border-t border-brand-paper-200 hover:bg-brand-paper-100"
                  >
                    <td className="px-4 py-2 font-medium">{label(c)}</td>
                    <td className="px-4 py-2 opacity-80 font-mono text-xs">
                      {c.handle ? `@${c.handle}` : "—"}
                    </td>
                    <td className="px-4 py-2 opacity-80">
                      {c.region ?? "—"}
                      {c.language ? ` · ${c.language}` : ""}
                    </td>
                    <td className="px-4 py-2 opacity-80">
                      {c.creator_approved_at ? (
                        c.creator_approved_at.slice(0, 10)
                      ) : (
                        <Badge variant="secondary">unknown</Badge>
                      )}
                    </td>
                    <td className="px-4 py-2 text-right">
                      <Button
                        variant="tertiary"
                        size="sm"
                        onClick={() => setPreviewCreator(c)}
                        aria-label={`View profile for ${label(c)}`}
                      >
                        View
                      </Button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </CardContent>
        </Card>
      )}

      <CreatorQuickViewDialog
        creator={previewCreator}
        onClose={() => setPreviewCreator(null)}
      />
    </div>
  );
}
